import * as analytic from '@anm/analytic';
import Loader from '@anm/components/Loader';
import CropModal from '@anm/components/modals/CropModal';
import StreamDetailsStep from '@anm/components/stream/NewStreamModal/components/StreamDetailsStep';
import usePreviewImage from '@anm/components/stream/NewStreamModal/hooks/usePreviewImage';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useState, FC } from 'react';

import { DestinationWithStreamMeta } from '../StreamItem';
import { ModalWrapper } from '../StreamModalWrapper';
import { NewStreamModalProps, StreamScreens } from '../types';

import ModalContent from './Wrapper';
import DiscardChangesModal from './components/DiscardChangesModal';
import EditStreamDestination from './components/EditStreamDestination';
import RecordingStep from './components/RecordingStep';
import StreamDestinations from './components/StreamDestinations';
import { getIsSameData } from './helpers';
import useStreamTime from './hooks/useStreamTime';
import useTitleMaxLength from './hooks/useTitleMaxLength';
import useUpdateDestinationParams from './hooks/useUpdateDestinationParams';

export * from '../types';

const NewStreamModal: FC<NewStreamModalProps> = ({
  path,
  name,
  date,
  isEdit,
  userId,
  isHosted,
  folderId,
  waveUrls,
  isWebinar,
  isScheduled,
  isUploading,
  description,
  isDuplicate,
  isSetupStream,
  isStreamOwner,
  defaultPreview,
  isWebinarEnabled,
  destinationsList,
  initialModalState,
  isDestinationTabs,
  destinationErrors,
  isEditDestinations,
  isSubmitPending,
  destinationParams,
  guestDestinationsIds,
  deleteGuestDestinations,
  selectedDestinationIds,
  isDestinationLimitMessage,
  canGuestAddDestination,
  isClickableBackdrop = false,
  isCloseButtonInsideModal = false,
  isDestinationsPending = false,
  onAddGuestDestinationChange,
  onClose,
  onDateChange,
  onSelectPath,
  onNameChange,
  onUploadUpdate,
  onCreateStream,
  toggleScheduled,
  onWebinarChange,
  onCreateRecording,
  onSelectDestination,
  onDescriptionChange,
  renderAddDestination,
  onSubmitDestinationMeta,
  onUpdateDestinationParams,
  onDeleteGuestDestination
}) => {
  const isRecording = initialModalState?.kind === 'RECORD';
  const [editDestinationId, setEditId] = useState<string>('');
  const defaultStep = isEditDestinations
    ? StreamScreens.stream_destinations
    : isHosted || isSetupStream
    ? StreamScreens.stream_details
    : StreamScreens.new_stream;
  const [step, setStep] = useState<StreamScreens>(defaultStep);

  const isEditDestination = !!editDestinationId;
  const selectedDestination =
    destinationsList?.find(d => d.id === editDestinationId) || ({} as DestinationWithStreamMeta);

  const defaultDestinationPreview =
    selectedDestination?.request?.preview || destinationParams?.[editDestinationId || '']?.preview;

  const [preview, destinationPreview, isCropModalOpen, createUpload, closeCropModal] = usePreviewImage({
    isEditDestination,
    defaultPreview,
    defaultDestinationPreview,
    onUploadUpdate
  });

  useUpdateDestinationParams({
    name,
    description,
    preview: preview.src,
    destinationParams,
    onUpdateDestinationParams
  });

  const cropModalProps = isEditDestination ? destinationPreview : preview;

  const isSameData =
    !deleteGuestDestinations?.length &&
    getIsSameData({
      name,
      date,
      preview,
      folderId,
      isWebinar,
      description,
      isScheduled,
      initialModalState,
      destinationParams,
      canGuestAddDestination,
      selectedDestinationIds,
      fbGroupIds: destinationsList.filter(d => d.kind === 'group' && d.network === 'FACEBOOK').map(d => d.id)
    });

  const handleModalClose = () => {
    if ((!isSameData || isUploading) && isEdit && !isDuplicate) {
      openDiscardModal();
    } else {
      onClose();
      analytic.trackNewStreamCloseDialog();
    }
  };

  const [isDiscardModalOpen, openDiscardModal, closeDiscardModal] = useSwitchState();

  const timeProps = useStreamTime(date, onDateChange);
  const savedDestinationPreview = !destinationParams?.[editDestinationId]?.preview
    ? destinationParams?.[editDestinationId]?.preview || preview.src
    : destinationParams?.[editDestinationId]?.preview || preview.src;
  const editDestinationName =
    destinationParams?.[editDestinationId]?.title || selectedDestination?.request?.title || name;
  const editDestinationDescription =
    destinationParams?.[editDestinationId]?.description || selectedDestination?.request?.description || description;

  const handelDeleteGuestDestinations = useCallback(
    (id: string) => {
      onSelectDestination(id);
      onDeleteGuestDestination?.(id);
    },
    [onSelectDestination, onDeleteGuestDestination]
  );

  const isNoDestinationsWarning = !selectedDestinationIds.length && !isWebinar;
  const isDisabledSubmitButton = isUploading || (isSameData && !isDuplicate) || isNoDestinationsWarning;

  const toStreamDetails = () => setStep(StreamScreens.stream_details);
  const titleMaxLength = useTitleMaxLength({ name, selectedDestinationIds, destinationsList, toStreamDetails });

  return (
    <ModalWrapper
      onModalClose={handleModalClose}
      isClickableBackdrop={isClickableBackdrop}
      cardSettings={{ borderRadius: 10 }}
      isCloseButtonInsideModal={isCloseButtonInsideModal}
    >
      <ModalContent>
        {isDiscardModalOpen && (
          <DiscardChangesModal
            onCancel={closeDiscardModal}
            onDiscard={() => (editDestinationId ? setEditId('') : onClose())}
          />
        )}
        {isCropModalOpen && (
          <CropModal
            {...cropModalProps}
            isEditDestination={!!editDestinationId}
            onModalClose={closeCropModal}
            onCreateUpload={createUpload}
          />
        )}
        {step === StreamScreens.new_stream && (
          <RecordingStep
            isDisabledSubmit={(isRecording && isSameData) || !!isHosted}
            onSubmit={onCreateRecording}
            isPending={isSubmitPending}
            onSetupNavigate={toStreamDetails}
            {...{ path, isEdit, isDuplicate, isRecording, onNameChange, name, onSelectPath }}
          />
        )}
        {step === StreamScreens.stream_details && (
          <StreamDetailsStep
            navigateTo={setStep}
            isBackButton={!isHosted}
            onPreviewSelect={createUpload}
            isPending={isUploading}
            {...{
              name,
              date,
              path,
              isEdit,
              preview,
              isHosted,
              titleMaxLength,
              isRecording,
              onSelectPath,
              isDuplicate,
              description,
              isScheduled,
              onDateChange,
              ...timeProps,
              onNameChange,
              isSetupStream,
              onCreateStream,
              toggleScheduled,
              isSubmitPending,
              onDescriptionChange,
              canGuestAddDestination,
              isDisabledSubmitButton,
              onAddGuestDestinationChange,
              destinationsList: destinationsList?.filter(d => selectedDestinationIds.includes(d.id))
            }}
          />
        )}
        {step === StreamScreens.stream_destinations && (
          <>
            {isDestinationsPending ? (
              <Loader size="small" color="blue" />
            ) : editDestinationId ? (
              <EditStreamDestination
                onOpenDiscardModal={openDiscardModal}
                onBack={() => setEditId('')}
                isPending={isUploading}
                canShowPreview={isScheduled}
                onPreviewSelect={createUpload}
                destination={selectedDestination}
                {...{ onSubmitDestinationMeta, editDestinationId }}
                name={editDestinationName}
                previewSrc={destinationPreview.src}
                savedPreview={savedDestinationPreview}
                description={editDestinationDescription}
                isFbInstruction={destinationParams?.[editDestinationId]?.isFbInstruction}
                privacy={destinationParams?.[editDestinationId]?.privacy || selectedDestination.request?.privacy}
                rotation={destinationParams?.[editDestinationId]?.transform || selectedDestination.request?.transform}
              />
            ) : (
              <StreamDestinations
                selectedIds={selectedDestinationIds}
                isPending={isSubmitPending}
                onEditDestination={setEditId}
                isNoDestinationsWarning={isNoDestinationsWarning}
                onDeleteGuestDestination={handelDeleteGuestDestinations}
                onBack={isEditDestinations ? undefined : () => setStep(StreamScreens.stream_details)}
                {...{
                  userId,
                  isEdit,
                  preview,
                  isHosted,
                  waveUrls,
                  isWebinar,
                  isWebinarEnabled,
                  onWebinarChange,
                  isScheduled,
                  isUploading,
                  isDuplicate,
                  isStreamOwner,
                  onCreateStream,
                  destinationsList,
                  destinationErrors,
                  isDestinationTabs,
                  isEditDestinations,
                  onSelectDestination,
                  guestDestinationsIds,
                  renderAddDestination,
                  isDisabledSubmitButton,
                  deleteGuestDestinations,
                  isDestinationLimitMessage
                }}
              />
            )}
          </>
        )}
      </ModalContent>
    </ModalWrapper>
  );
};

export default NewStreamModal;
